import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Users, 
  Edit, 
  Trash2, 
  Upload,
  Download,
  Star,
  Loader2,
  AlertCircle,
  CheckCircle,
  X,
  ChevronDown,
  ChevronRight,
  Trophy,
  Crown
} from 'lucide-react';
import TeamImportModal from './TeamImportModal';
import TeamEditModal from './TeamEditModal';
import { normalizePokemonData, normalizeMoveName } from '../../utils/pokemonDataUtils';
import { getPokemonImage, createPokemonImageErrorHandler, useImageMapReady } from '../../utils/pokemonImageResolver';
import { getApiBaseUrl } from '../../utils/apiUrl';

const TeamManagement = () => {
  // Force re-render when image map is ready
  const imageMapReady = useImageMapReady();
  
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLeague, setFilterLeague] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'top', 'popular'
  const [leagues, setLeagues] = useState([]); // Store leagues for filter dropdown

  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const fetchTeams = useCallback(async (page = 1, search = '', league = '', tier = '', sortBy = 'name', sortOrder = 'asc') => {
    try {
      setLoading(true);
      
      // Override tier filter based on viewMode
      let tierFilter = '';
      if (viewMode === 'top') {
        tierFilter = 'TOP';
      } else if (viewMode === 'popular') {
        tierFilter = 'POPULAR';
      } else if (viewMode === 'all') {
        tierFilter = ''; // Show all
      } else {
        tierFilter = tier; // Use manual filter
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.itemsPerPage.toString(),
        search,
        league,
        sortBy,
        sortOrder
      });
      
      // Only add tier filter if not empty
      if (tierFilter) {
        params.append('tier', tierFilter);
      }

      console.log('[FRONTEND] Fetching teams with params:', Object.fromEntries(params));
      const response = await fetch(`${getApiBaseUrl()}/api/teams?${params}`);
      
      if (!response.ok) {
        console.error('[FRONTEND] Response not OK:', response.status, response.statusText);
        setError(`HTTP ${response.status}: ${response.statusText}`);
        return;
      }
      
      const data = await response.json();
      console.log('[FRONTEND] Response data:', data);

      if (data.success) {
        const teamsData = data.data.teams || data.data || [];
        const paginationData = data.data.pagination || {
          currentPage: page,
          totalPages: Math.ceil(teamsData.length / pagination.itemsPerPage),
          totalItems: teamsData.length,
          itemsPerPage: pagination.itemsPerPage
        };
        
        console.log('[FRONTEND] Fetched teams:', teamsData.length, 'pagination:', paginationData);
        
        if (teamsData.length === 0) {
          console.warn('[FRONTEND] No teams found! Check server logs for details.');
          // Try debug endpoint
          try {
            const debugResponse = await fetch(`${getApiBaseUrl()}/api/teams/debug/all`);
            const debugData = await debugResponse.json();
            console.log('[FRONTEND] Debug endpoint result:', debugData);
            if (debugData.data && debugData.data.total > 0) {
              console.warn('[FRONTEND] Data exists in DB but not showing! Check filters.');
            }
          } catch (debugError) {
            console.warn('[FRONTEND] Could not check debug endpoint:', debugError);
          }
        }
        
        setTeams(teamsData);
        setPagination(paginationData);
        setError(null);
      } else {
        setError(data.error || 'Failed to fetch teams');
        console.error('[FRONTEND] Failed to fetch teams:', data);
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination.itemsPerPage, viewMode]);

  // Fetch leagues on component mount (same as admin/leagues)
  useEffect(() => {
    const fetchLeaguesForFilter = async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '100',
          status: 'active', // Only active leagues
          sortBy: 'name',
          sortOrder: 'asc'
        });

        const response = await fetch(`${getApiBaseUrl()}/api/leagues?${params}`);
        const data = await response.json();

        if (data.success) {
          setLeagues(data.data.leagues || data.data || []);
        }
      } catch (err) {
        console.error('Error fetching leagues for filter:', err);
      }
    };

    fetchLeaguesForFilter();
  }, []);

  useEffect(() => {
    fetchTeams(pagination.currentPage, searchTerm, filterLeague, filterTier, sortBy, sortOrder);
  }, [pagination.currentPage, searchTerm, filterLeague, filterTier, sortBy, sortOrder, viewMode, fetchTeams]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleFilter = (field, value) => {
    if (field === 'league') {
      setFilterLeague(value);
    } else if (field === 'tier') {
      setFilterTier(value);
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // const handleSort = (field) => {
  //   if (sortBy === field) {
  //     setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortBy(field);
  //     setSortOrder('asc');
  //   }
  // };

  const handleSelectTeam = (id) => {
    setSelectedTeams(prev => 
      prev.includes(id) 
        ? prev.filter(t => t !== id)
        : [...prev, id]
    );
  };

  // const handleSelectAll = () => {
  //   if (selectedTeams.length === teams.length) {
  //     setSelectedTeams([]);
  //   } else {
  //     setSelectedTeams(teams.map(t => t.id));
  //   }
  // };

  const handleDelete = async (id) => {
    if (window.confirm('คุณแน่ใจหรือไม่ที่จะลบทีมนี้?')) {
      try {
        const response = await fetch(`${getApiBaseUrl()}/api/teams/${id}`, {
          method: 'DELETE'
        });

        if (response.ok) {
          showNotification('ลบทีมสำเร็จ', 'success');
          fetchTeams(pagination.currentPage, searchTerm, filterLeague, filterTier, sortBy, sortOrder);
        } else {
          showNotification('ลบทีมไม่สำเร็จ', 'error');
        }
      } catch (error) {
        showNotification('เกิดข้อผิดพลาดในการลบ', 'error');
      }
    }
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleImportSuccess = async () => {
    showNotification('นำเข้าทีมสำเร็จ', 'success');
    
    // First, check debug endpoint to see if data exists
    try {
      const debugResponse = await fetch(`${getApiBaseUrl()}/api/teams/debug/all`);
      const debugData = await debugResponse.json();
      console.log('[IMPORT SUCCESS] Debug check - Total teams in DB:', debugData.data?.total || 0);
      if (debugData.data?.teams && debugData.data.teams.length > 0) {
        console.log('[IMPORT SUCCESS] Sample teams:', debugData.data.teams.slice(0, 3));
      }
    } catch (debugError) {
      console.warn('[IMPORT SUCCESS] Could not check debug endpoint:', debugError);
    }
    
    // Reset filters and refresh teams list
    setSearchTerm('');
    setFilterLeague('');
    setFilterTier('');
    setViewMode('all');
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    
    // Force refresh by calling fetchTeams directly
    setTimeout(() => {
      fetchTeams(1, '', '', '', 'weight', 'desc');
    }, 500);
  };

  const handleExport = () => {
    showNotification('กำลังส่งออกทีมแนะนำ...', 'success');
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'TOP': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'POPULAR': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'S': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'A': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'B': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'C': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'D': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getTierLabel = (tier) => {
    switch (tier) {
      case 'TOP': return 'Top Team';
      case 'POPULAR': return 'ทีมยอดนิยม';
      default: return tier || 'N/A';
    }
  };

  const TeamCard = ({ team }) => (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={`bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-lg border-2 border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-300 ${
        selectedTeams.includes(team.id) ? 'ring-4 ring-blue-500 border-blue-500' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={selectedTeams.includes(team.id)}
            onChange={() => handleSelectTeam(team.id)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-pink-100 dark:from-red-900/30 dark:to-pink-900/30 rounded-lg flex items-center justify-center">
            <Users className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
            className="p-2 text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {expandedTeam === team.id ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => {
              setEditingTeam(team);
              setShowEditModal(true);
            }}
            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(team.id)}
            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
                          <h3 className={`font-bold text-gray-900 dark:text-white ${
                            'text-xl sm:text-2xl leading-relaxed'
                          }`}>
            {team.team_name || team.team_name_th || (team.team_number ? `Team #${team.team_number}` : `Team #${team.id}`)}
          </h3>
          <span className={`px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${getTierColor(team.recommendation_tier || team.tier)}`}>
            {team.recommendation_tier === 'TOP' && <Crown className="w-3 h-3" />}
            {getTierLabel(team.recommendation_tier || team.tier)}
          </span>
        </div>

        {team.description && (
          <p className="text-gray-600 dark:text-gray-400 text-sm line-clamp-2">
            {team.description}
          </p>
        )}

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center space-x-2">
            <Trophy className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              {team.league_name || 'N/A'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Star className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600 dark:text-gray-400">
              คะแนน: {team.score || 0}
            </span>
          </div>
        </div>

        {team.pokemon && team.pokemon.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {team.pokemon.slice(0, 3).map((pokemon, index) => {
              const normalizedPoke = normalizePokemonData(pokemon, 'th');
              const pokemonName = normalizedPoke?.pokemon_name || normalizedPoke?.name_th || pokemon.name_th || pokemon.name || pokemon.speciesId || pokemon.pokemon_id;
              return (
                <span
                  key={index}
                  className="px-3 py-1.5 text-sm sm:text-base font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full"
                >
                  {pokemonName}
                </span>
              );
            })}
            {team.pokemon.length > 3 && (
              <span className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full">
                +{team.pokemon.length - 3}
              </span>
            )}
          </div>
        )}

        <AnimatePresence>
          {expandedTeam === team.id && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3"
            >
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">รายละเอียด</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {team.description || 'ไม่มีรายละเอียด'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">ลีก:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {team.league_name || 'N/A'}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Tier:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {getTierLabel(team.recommendation_tier || team.tier)}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">Weight:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {team.weight || 0}
                  </span>
                </div>
                <div>
                  <span className="font-medium text-gray-900 dark:text-white">อัตราชนะ:</span>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">
                    {team.win_rate ? `${team.win_rate}%` : 'N/A'}
                  </span>
                </div>
              </div>

              {team.pokemon && team.pokemon.length > 0 && (
                <div>
                  <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-4">โปเกมอนในทีม</h4>
                  <div className="space-y-3">
                    {team.pokemon.map((pokemon, index) => {
                      // Normalize Pokemon data with mapping
                      const normalizedPoke = normalizePokemonData(pokemon, 'th');
                      const pokemonId = normalizedPoke?.pokemon_id || pokemon.pokemon_id || pokemon.speciesId || pokemon.id;
                      const pokemonName = normalizedPoke?.pokemon_name || normalizedPoke?.name_th || pokemon.name_th || pokemon.name || pokemon.speciesId || `Pokemon ${index + 1}`;
                      // Use pokemonImageResolver - SINGLE SOURCE OF TRUTH
                      // No imageSources - using getPokemonImage directly
                      const primaryImageUrl = getPokemonImage(normalizedPoke || pokemon, 'default');
                      const handleImageError = createPokemonImageErrorHandler(normalizedPoke || pokemon, { variant: 'default' });
                      
                      return (
                        <div key={index} className="flex items-center space-x-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 dark:from-gray-800 dark:to-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                          {/* Pokemon Image - ใหญ่ขึ้น */}
                          {primaryImageUrl ? (
                            <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-white dark:bg-gray-800 rounded-xl flex items-center justify-center border-2 border-gray-300 dark:border-gray-600 shadow-sm overflow-hidden">
                              <img
                                src={primaryImageUrl}
                                alt={pokemonName}
                                className="w-full h-full object-contain p-2"
                                onError={handleImageError}
                                loading="lazy"
                              />
                            </div>
                          ) : (
                            <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                              <span className="text-base font-bold text-gray-600 dark:text-gray-300">{index + 1}</span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            {/* Pokemon Name - ใหญ่ขึ้น อ่านง่าย */}
                            <div className="font-bold text-lg sm:text-xl text-gray-900 dark:text-white mb-2 truncate">
                              {pokemonName}
                            </div>
                            {/* Moves - ใหญ่ขึ้น */}
                            {(normalizedPoke?.fast_move || normalizedPoke?.charged_move_1 || pokemon.fast_move || pokemon.charged_move_1) && (
                              <div className="space-y-1">
                                {normalizedPoke?.fast_move && (
                                  <div className="text-base text-gray-700 dark:text-gray-300">
                                    <span className="font-semibold">Fast:</span> {normalizeMoveName(normalizedPoke.fast_move)}
                                  </div>
                                )}
                                {(normalizedPoke?.charged_move_1 || normalizedPoke?.charged_move_2) && (
                                  <div className="text-base text-gray-700 dark:text-gray-300">
                                    <span className="font-semibold">Charged:</span> {[normalizedPoke.charged_move_1, normalizedPoke.charged_move_2].filter(Boolean).map(normalizeMoveName).join(', ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {team.strategy && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">กลยุทธ์</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {team.strategy}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">เกิดข้อผิดพลาด</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => fetchTeams()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            ลองใหม่
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
              notification.type === 'success' 
                ? 'bg-green-100 text-green-800 border border-green-200' 
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 hover:opacity-75"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">ทีมแนะนำ</h1>
          <p className="text-gray-600 dark:text-gray-400">จัดการทีมแนะนำและกลยุทธ์การเล่น</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleImport}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            <span>นำเข้าทีม</span>
          </button>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>ส่งออก</span>
          </button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setViewMode('all')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            viewMode === 'all'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          ทั้งหมด
        </button>
        <button
          onClick={() => setViewMode('top')}
          className={`px-4 py-2 text-sm font-medium transition-colors flex items-center space-x-1 ${
            viewMode === 'top'
              ? 'text-yellow-600 dark:text-yellow-400 border-b-2 border-yellow-600 dark:border-yellow-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <Crown className="w-4 h-4" />
          <span>Top Teams</span>
        </button>
        <button
          onClick={() => setViewMode('popular')}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            viewMode === 'popular'
              ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          ทีมยอดนิยม
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาทีม..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <Filter className="w-4 h-4" />
              <span>ตัวกรอง</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    ลีก
                  </label>
                  <select
                    value={filterLeague}
                    onChange={(e) => handleFilter('league', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">ทั้งหมด</option>
                    {leagues.map((league) => (
                      <option key={league.id} value={league.id}>
                        {league.name_th || league.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tier
                  </label>
                  <select
                    value={filterTier}
                    onChange={(e) => handleFilter('tier', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">ทั้งหมด</option>
                    <option value="S">Tier S</option>
                    <option value="A">Tier A</option>
                    <option value="B">Tier B</option>
                    <option value="C">Tier C</option>
                    <option value="D">Tier D</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    เรียงตาม
                  </label>
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="name-asc">ชื่อทีม (A-Z)</option>
                    <option value="name-desc">ชื่อทีม (Z-A)</option>
                    <option value="score-asc">คะแนน (น้อยไปมาก)</option>
                    <option value="score-desc">คะแนน (มากไปน้อย)</option>
                    <option value="tier-asc">Tier (A-Z)</option>
                    <option value="tier-desc">Tier (Z-A)</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bulk Actions */}
      {selectedTeams.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-blue-800 dark:text-blue-200 font-medium">
              เลือกแล้ว {selectedTeams.length} รายการ
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedTeams([])}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {teams.map((team) => (
            <TeamCard key={team.id} team={team} />
          ))}
        </AnimatePresence>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            แสดง {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} ถึง{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} จาก{' '}
            {pagination.totalItems} รายการ
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
              disabled={pagination.currentPage === 1}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ก่อนหน้า
            </button>
            <span className="px-3 py-2 text-gray-700 dark:text-gray-300">
              หน้า {pagination.currentPage} จาก {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
              disabled={pagination.currentPage === pagination.totalPages}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ถัดไป
            </button>
          </div>
        </div>
      )}

      {/* Import Modal */}
      <TeamImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Edit Modal */}
      {showEditModal && editingTeam && (
        <TeamEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingTeam(null);
          }}
          team={editingTeam}
          onSaveSuccess={() => {
            fetchTeams(pagination.currentPage, searchTerm, filterLeague, filterTier, sortBy, sortOrder);
            showNotification('แก้ไขทีมสำเร็จ', 'success');
          }}
        />
      )}
    </div>
  );
};

export default TeamManagement;
